import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MainLayout } from '../../../core/layouts/MainLayout';
import { MapPin, TrendingUp, Home, Building2, Share2 } from 'lucide-react';
import { CtaSection } from '../../landing/components/CtaSection';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { Button } from '../../../core/components/Button';
import { useArea, useProjects } from '../../../core/api';
import { FeaturedUnitsInArea } from '../components/FeaturedUnitsInArea';
import { RemoteImage } from '../../../core/components/RemoteImage';

export const AreaDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: area, isLoading, error } = useArea(id);

    // Fetch projects in this area (use area.id once loaded)
    // Fetch projects in this area (use area.id once loaded)
    const { data: projectsResponse } = useProjects({
        areaId: area?.id,
        enabled: !!area?.id,
    });
    const projects = Array.isArray(projectsResponse) ? projectsResponse : [];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    // Loading state
    if (isLoading) {
        return (
            <MainLayout>
                <div className="h-screen flex items-center justify-center bg-surface">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-secondary/20 border-t-secondary rounded-full animate-spin" />
                        <p className="text-slate-500 font-medium">{t('common.loading', 'Loading...')}</p>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!area || error) {
        return (
            <div className="h-screen flex items-center justify-center bg-surface">
                <div className="text-center">
                    <h2 className="text-3xl font-display font-bold text-secondary mb-4">{t('errors.areaNotFound', 'Area Not Found')}</h2>
                    <Button onClick={() => navigate('/')} variant="primary">
                        {t('common.goHome', 'Go Home')}
                    </Button>
                </div>
            </div>
        );
    }

    const areaName = area.name || area.slug;
    const areaDescription = area.description || '';

    return (
        <MainLayout>

            {/* 1. Cinematic Hero Section */}
            <div className="relative h-[50vh] sm:h-[65vh] md:h-[80vh] w-full bg-secondary overflow-hidden">
                <div className="absolute inset-0">
                    <RemoteImage
                        src={area.image}
                        alt={areaName}
                        className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary via-transparent to-transparent" />
                    <div className="absolute inset-0 bg-black/20" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24 z-20">
                    <div className="flex items-center gap-3 mb-4 animate-fade-in-up">
                        <span className="bg-white/10 backdrop-blur-md text-white text-xs md:text-sm font-bold px-3 py-1 uppercase tracking-widest border border-white/20">
                            {t('area.destination', 'DESTINATION')}
                        </span>
                        <div className="hidden sm:block h-px w-12 bg-white/40"></div>
                        <span className="text-slate-300 text-xs md:text-sm font-medium uppercase tracking-wider flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {areaName}
                        </span>
                    </div>

                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight animate-fade-in-up delay-100">
                        {areaName}
                    </h1>
                </div>
            </div>

            <div className="bg-surface min-h-screen">
                <div className="container mx-auto px-4 md:px-12 lg:px-24 py-12 md:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                        {/* Main Content (Left) */}
                        <div className="lg:col-span-8 space-y-16">

                            {/* Intro */}
                            <section>
                                <h3 className="text-2xl font-display font-bold text-secondary mb-6 flex items-center gap-3">
                                    <span className="w-8 h-[2px] bg-secondary block"></span>
                                    {t('area.about', 'The Location')}
                                </h3>
                                <div
                                    className="prose prose-lg max-w-none text-slate-600 leading-relaxed font-light"
                                    dangerouslySetInnerHTML={{ __html: areaDescription }}
                                />
                            </section>

                            {/* Projects in Area Grid */}
                            <section>
                                <h3 className="text-2xl font-display font-bold text-secondary mb-8 flex items-center gap-3">
                                    <span className="w-8 h-[2px] bg-secondary block"></span>
                                    {t('area.signatureProjects', { areaName })}
                                </h3>

                                {projects.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {projects.map((project: any) => {
                                            const projectTitle = project.title || project.slug;

                                            return (
                                                <Link to={`/projects/${project.slug}`} key={project.id} className="group block relative">
                                                    <div className="bg-white border border-slate-100 overflow-hidden shadow-hesn transition-all duration-500 group-hover:shadow-lg">
                                                        <div className="relative h-48 overflow-hidden">
                                                            <RemoteImage src={project.image} alt={projectTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                                            <div className="absolute inset-0 bg-secondary/0 group-hover:bg-secondary/10 transition-colors" />
                                                        </div>
                                                        <div className="p-5">
                                                            <h4 className="text-xl font-bold text-secondary mb-2 group-hover:text-primary transition-colors">{projectTitle}</h4>
                                                            <div className="flex items-center text-slate-500 text-sm">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                                                                {t('common.viewDetails', 'View Details')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="p-8 border border-dashed border-slate-300 text-center rounded-hesn">
                                        <p className="text-slate-400 italic">{t('area.noProjects', 'Curating projects for this area...')}</p>
                                    </div>
                                )}
                            </section>

                            {/* Added: Featured Units in Area */}
                            <FeaturedUnitsInArea areaId={area.id} areaName={areaName} />
                        </div>

                        {/* Right: Stats Sidebar */}
                        <div className="lg:col-span-4">
                            <div className="sticky top-28 space-y-8">
                                <div className="bg-white border border-slate-200 p-8 shadow-sm">
                                    <h3 className="font-display font-bold text-secondary text-xl mb-6 pb-4 border-b border-slate-100 uppercase tracking-wider">
                                        {t('area.marketStats', 'Market Pulse')}
                                    </h3>

                                    <div className="space-y-6">
                                        <div className="flex items-center gap-5 group">
                                            <div className="w-12 h-12 bg-slate-50 flex items-center justify-center text-secondary border border-slate-200 group-hover:border-secondary transition-colors">
                                                <Home className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-0.5">{t('area.properties', 'Properties')}</p>
                                                <p className="text-xl font-display font-bold text-secondary">{projects.length}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-5 group">
                                            <div className="w-12 h-12 bg-slate-50 flex items-center justify-center text-secondary border border-slate-200 group-hover:border-secondary transition-colors">
                                                <TrendingUp className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-0.5">{t('area.avgPrice', 'Avg. Price')}</p>
                                                <p className="text-xl font-display font-bold text-secondary">
                                                    {area.stats?.avgPrice ? `${area.stats.avgPrice.toLocaleString()} ${t('common.currency', 'EGP')}` : '—'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-5 group">
                                            <div className="w-12 h-12 bg-slate-50 flex items-center justify-center text-secondary border border-slate-200 group-hover:border-secondary transition-colors">
                                                <Building2 className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-0.5">{t('area.growth', 'Growth Rate')}</p>
                                                <p className="text-xl font-display font-bold text-green-600">
                                                    {area.stats?.growth ? `+${area.stats.growth}%` : '—'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-slate-100 flex justify-center">
                                        <button
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: areaName,
                                                        text: `Check out ${areaName} on El Hesn`,
                                                        url: window.location.href,
                                                    }).catch(console.error);
                                                } else {
                                                    navigator.clipboard.writeText(window.location.href);
                                                    alert(t('common.linkCopied', 'Link copied to clipboard'));
                                                }
                                            }}
                                            className="text-slate-400 hover:text-secondary transition-colors p-2 flex items-center gap-2"
                                        >
                                            <Share2 className="w-5 h-5" />
                                            <span className="text-sm">{t('common.share', 'Share')}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CtaSection />
        </MainLayout>
    );
};
